import { presentationBackground } from './presentationBackground';

/**
 * Parent portal palette: brand teal as anchor, plus soft complementary accents
 * (sky, gold, lavender, mint, coral) for distinct cards and event chips.
 */
export const parentPortalTheme = {
  ...presentationBackground,

  pageBackground:
    'linear-gradient(145deg, #B8E4F0 0%, #D4EEF8 22%, #FFF0D4 48%, #D8F0EA 72%, #C5ECF6 100%)',

  backgroundOrbs: [
    { top: '-18%', insetInlineStart: '-12%', size: '58vmin', color: 'rgba(73, 185, 217, 0.38)' },
    { bottom: '-22%', insetInlineEnd: '-10%', size: '62vmin', color: 'rgba(248, 222, 138, 0.42)' },
    { top: '38%', insetInlineEnd: '-14%', size: '36vmin', color: 'rgba(221, 209, 246, 0.38)' },
    { bottom: '8%', insetInlineStart: '4%', size: '28vmin', color: 'rgba(205, 239, 224, 0.45)' },
  ] as Array<{
    top?: string;
    bottom?: string;
    insetInlineStart?: string;
    insetInlineEnd?: string;
    size: string;
    color: string;
  }>,

  text: {
    primary: '#1a365d',
    secondary: '#475569',
  },

  /** Dashboard home cards — one accent each, all harmonized with brand teal */
  card: {
    colors: ['#C7E6F7', '#F8DE8A', '#DDD1F6', '#CDEFE0'],
    border: 'rgba(38, 155, 178, 0.2)',
    shadow: '0 12px 28px rgba(38, 155, 178, 0.14)',
  },

  settings: {
    passwordCard: '#DDD1F6',
    emailCard: '#CDEFE0',
    emailNote: presentationBackground.primaryDark,
  },

  table: {
    headerBg: presentationBackground.primaryDark,
    rowAlt: 'rgba(199, 230, 247, 0.35)',
    border: 'rgba(38, 155, 178, 0.18)',
  },

  preview: {
    bannerBg: '#C7E6F7',
    bannerColor: presentationBackground.primaryDark,
  },

  subscribe: {
    panelBg: '#FFF0D4',
    planSelected: '#C7E6F7',
    planBorder: presentationBackground.primaryDark,
    bestValueChip: '#F8DE8A',
    activePanel: '#CDEFE0',
  },

  /** Event filter chips & row badges — distinct hue per type */
  eventTypes: {
    birthday: '#F8DE8A',
    nursery_event: '#A7E5DE',
    graduation: '#BDE7D7',
    holiday: '#F5C56E',
    field_trip: '#F6B6BB',
    special_event: '#D8C7F7',
  },

  stripe: {
    text: '#1a365d',
    placeholder: '#94a3b8',
  },

  skeleton: {
    rowAlt: 'rgba(199, 230, 247, 0.35)',
    shimmer: 'rgba(38, 155, 178, 0.1)',
  },

  decorative: [
    { top: '9%', left: '5%', size: 14, color: '#F6B6BB', shape: 'circle' as const },
    { top: '18%', right: '8%', size: 18, color: '#F8DE8A', shape: 'diamond' as const },
    { top: '52%', left: '3%', size: 12, color: '#DDD1F6', shape: 'dot' as const },
    { bottom: '14%', right: '6%', size: 20, color: '#7ED5EA', shape: 'triangle' as const },
    { bottom: '8%', left: '12%', size: 11, color: '#CDEFE0', shape: 'circle' as const },
    { top: '38%', right: '2%', size: 10, color: '#F5C56E', shape: 'dot' as const },
    { top: '72%', right: '18%', size: 16, color: '#D8C7F7', shape: 'diamond' as const },
    { bottom: '28%', left: '8%', size: 13, color: '#49B9D9', shape: 'circle' as const },
  ] as Array<{
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    size: number;
    color: string;
    shape: 'circle' | 'diamond' | 'dot' | 'triangle';
  }>,

  button: {
    cancel: '#991b1b',
    primaryShadow: '0 4px 14px rgba(73, 185, 217, 0.35)',
    primaryShadowHover: '0 6px 20px rgba(73, 185, 217, 0.45)',
  },
} as const;

export const parentPortalPrimaryButtonSx = {
  background: parentPortalTheme.sidebarGradient,
  boxShadow: parentPortalTheme.button.primaryShadow,
  '&:hover': {
    background: 'linear-gradient(135deg, #269BB2 0%, #1F8499 100%)',
    boxShadow: parentPortalTheme.button.primaryShadowHover,
    transform: 'translateY(-1px)',
  },
} as const;

export const parentPortalSolidButtonSx = {
  borderRadius: 999,
  bgcolor: parentPortalTheme.primaryDark,
  color: '#fff',
  '&:hover': { bgcolor: '#1F8499' },
} as const;
